import React from "react";
import { Box } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS, TYPE, VISUALLY_HIDDEN } from "../../utils/designTokens";
import { FilterChip } from "./listUi";
import RestaurantListCard from "./RestaurantListCard";

export const SORTS = [
  { key: "nearest", label: "Nearest" },
  { key: "fastest", label: "Fastest" },
  { key: "deals", label: "Most deals" },
];

function SortSelect({ value, onChange }) {
  return (
    <Box sx={{ position: "relative", flexShrink: 0 }}>
      <Box component="label" htmlFor="rl-sort" sx={VISUALLY_HIDDEN}>
        Sort restaurants
      </Box>
      <Box
        component="select"
        id="rl-sort"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          appearance: "none",
          height: 40,
          pl: 2,
          pr: 5,
          borderRadius: RADIUS.pill,
          border: `1px solid ${C.borderStrong}`,
          bgcolor: C.bg,
          color: C.ink,
          fontFamily: FONT_BODY,
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
          ...FOCUS_RING,
        }}
      >
        {SORTS.map((s) => (
          <option key={s.key} value={s.key}>
            {`Sort: ${s.label}`}
          </option>
        ))}
      </Box>
      <ExpandMoreRoundedIcon aria-hidden="true" sx={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 20, color: C.muted, pointerEvents: "none" }} />
    </Box>
  );
}

/**
 * The grid of restaurants, with its filters and sort. The list arrives already
 * filtered and sorted; `filters` is { openNow, deals, favourites, quick } and
 * `onFilter(key)` toggles one.
 */
function AllRestaurants({
  title,
  restaurants,
  dealCounts,
  filters,
  onFilter,
  showFavourites,
  sort,
  onSort,
  showControls = true,
  onClear,
  onOpen,
  onMessage,
}) {
  const n = restaurants.length;
  return (
    <Box component="section" aria-labelledby="all-title">
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", columnGap: 3, rowGap: 1.5, mb: 2.5 }}>
        <Box component="h2" id="all-title" sx={{ ...TYPE.sectionTitle, m: 0, color: C.ink, minWidth: 0 }}>
          {title}
          <Box component="span" sx={{ ...TYPE.body, fontSize: 15, fontWeight: 500, color: C.muted, ml: 1, letterSpacing: 0 }}>
            · {n} {n === 1 ? "place" : "places"}
          </Box>
        </Box>
        {showControls && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            <Box role="group" aria-label="Filter restaurants" sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <FilterChip pressed={filters.openNow} onClick={() => onFilter("openNow")}>Open now</FilterChip>
              <FilterChip pressed={filters.deals} onClick={() => onFilter("deals")}>Has deals</FilterChip>
              {showFavourites && (
                <FilterChip pressed={filters.favourites} onClick={() => onFilter("favourites")}>Favourites</FilterChip>
              )}
              <FilterChip pressed={filters.quick} onClick={() => onFilter("quick")}>Under 15 min</FilterChip>
            </Box>
            <SortSelect value={sort} onChange={onSort} />
          </Box>
        )}
      </Box>

      {n === 0 ? (
        <Box
          sx={{
            border: `1.5px dashed ${C.borderStrong}`,
            borderRadius: "18px",
            px: 3,
            py: 6,
            textAlign: "center",
          }}
        >
          <Box component="p" sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 17, fontWeight: 700, color: C.ink }}>
            No restaurants match these filters
          </Box>
          <Box component="p" sx={{ ...TYPE.body, m: 0, mt: 0.75, color: C.muted }}>
            Try another cuisine, or schedule an order for later.
          </Box>
          {onClear && (
            <Box
              component="button"
              type="button"
              onClick={onClear}
              sx={{
                ...BUTTON_RESET,
                ...FOCUS_RING,
                mt: 2.5,
                height: 44,
                px: 2.5,
                borderRadius: RADIUS.pill,
                bgcolor: C.ink,
                color: "#FFFFFF",
                fontFamily: FONT_BODY,
                fontSize: 14,
                fontWeight: 700,
                "&:hover": { bgcolor: "#000000" },
              }}
            >
              Clear filters
            </Box>
          )}
        </Box>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 280px), 1fr))", gap: "24px 20px" }}>
          {restaurants.map((r) => (
            <Box component="li" key={r._id} sx={{ minWidth: 0 }}>
              <RestaurantListCard r={r} dealCount={dealCounts.get(String(r._id)) || 0} onOpen={onOpen} onMessage={onMessage} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default React.memo(AllRestaurants);
